import React, {
    useCallback,
    FunctionComponent,
    useState,
    useEffect,
    useLayoutEffect,
    useMemo,
    useContext,
    useRef,
} from 'react';
import { faChevronLeft, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Car from '../../../../Assets/Images/detailsCar.png';
import CarComplete from '../../../../Assets/Images/detailsCarComplete.png';
import { db, firebase, functions } from '../../../../firebase';
import { AuthContext } from '../../../../Providers/UserProvider';
import { rejectionMessage } from '../../../../utils/rejectionMessage';
import StatusEmptyCard from '../../../StatusPage/StatusEmptyCard';
import {
    canCallCompleteJob,
    completePwaJob,
    PWA_COMPLETE_BUSY,
    PWA_COMPLETE_CONFIRM,
    pwaCompleteCopy,
} from '../../completePwaJob';
import { ordersJobDateTimeLabel, pwaLiveWriteRegionError } from '../../ordersSurface';
import { shopPhoneE164 } from './shopPhoneE164';

interface Props {
    jobDetailsId: string;
    onClose: () => void;
}

const ACCEPT_WRITE_ERROR = 'This job could not be accepted.';
const REJECT_WRITE_ERROR = 'This job could not be rejected.';
const NOT_PENDING = 'This job is no longer pending.';
const NOT_ASSIGNED = 'You are no longer assigned to this job.';

function acceptedJobWriteMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

/** Missing notes are ''. A template of `undefined` would write the literal word. */
function rejectionNotes(existingNotes: unknown, techName: unknown, reason: unknown): string {
    const prior = existingNotes == null ? '' : String(existingNotes);
    return `${prior} Rejected by ${techName}: ${reason}.`;
}

const REJECTED_ENTRY_KEYS = [
    'type',
    'damage',
    'shop',
    'waiting',
    'contact',
    'dropped',
    'date',
    'timezone',
    'timeZone',
] as const;

type RejectedEntryKey = typeof REJECTED_ENTRY_KEYS[number];

type RejectedVehicle = {
    year?: unknown;
    make?: unknown;
    model?: unknown;
    insurance?: unknown;
};

/** Fields the Rejected tab row and RejectedJobModal actually read. */
type RejectedListEntry = {
    id: string;
    status: 'rejected';
    vehicle?: RejectedVehicle;
} & Partial<Record<RejectedEntryKey, unknown>>;

function rejectedListEntry(job: any, jobId: string): RejectedListEntry {
    const entry: RejectedListEntry = { id: jobId, status: 'rejected' };
    if (!job || typeof job !== 'object') return entry;
    REJECTED_ENTRY_KEYS.forEach((key) => {
        if (job[key] !== undefined) entry[key] = job[key];
    });
    const vehicle = rejectedVehicle(job.vehicle);
    if (vehicle) entry.vehicle = vehicle;
    return entry;
}

function rejectedVehicle(vehicle: unknown): RejectedVehicle | undefined {
    if (!vehicle || typeof vehicle !== 'object' || Array.isArray(vehicle)) return undefined;
    const source = vehicle as Record<string, unknown>;
    const next: RejectedVehicle = {};
    (['year', 'make', 'model', 'insurance'] as const).forEach((key) => {
        if (source[key] !== undefined) next[key] = source[key];
    });
    return Object.keys(next).length > 0 ? next : undefined;
}

function valuesEqual(left: unknown, right: unknown): boolean {
    if (left === right) return true;
    if (left instanceof Date || right instanceof Date) {
        return left instanceof Date && right instanceof Date && left.getTime() === right.getTime();
    }
    if (typeof (left as { isEqual?: unknown })?.isEqual === 'function') {
        try {
            if ((left as { isEqual: (other: unknown) => boolean }).isEqual(right)) return true;
        } catch {
            return false;
        }
    }
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
        return left.every((item, index) => valuesEqual(item, right[index]));
    }
    if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => valuesEqual(leftRecord[key], rightRecord[key]));
}

function staleRejectedEntries(stored: unknown, jobId: string, nextEntry: RejectedListEntry): any[] {
    if (!Array.isArray(stored)) return [];
    return stored.filter((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        if ((entry as { id?: unknown }).id !== jobId) return false;
        return !valuesEqual(entry, nextEntry);
    });
}

function rejectedEntriesForJob(stored: unknown, jobId: string): any[] {
    if (!Array.isArray(stored)) return [];
    return stored.filter((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        return (entry as { id?: unknown }).id === jobId;
    });
}

const TABBABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Controls a keyboard user can reach inside the sheet, in tab order. */
function tabbableWithin(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((element) => {
        if (element.tabIndex < 0) return false;
        if (element.hasAttribute('disabled')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        return true;
    });
}

function focusIfConnected(element: HTMLElement | null) {
    if (element && document.body.contains(element)) {
        element.focus();
    }
}

const AcceptedJobModal: FunctionComponent<Props> = (Props) => {
    const { techId, tech, jobsRef, shopsRef, techsRef, collectionPrefix } = useContext(AuthContext);
    const regionId = String(collectionPrefix || '').replace(/^\/regions\//, '');
    const regionError = pwaLiveWriteRegionError(regionId);
    const [completeBusy, setCompleteBusy] = useState(false);
    const [completeError, setCompleteError] = useState('');
    const [completeCancelled, setCompleteCancelled] = useState(false);
    const completeCopy = pwaCompleteCopy(completeCancelled ? 'cancelled' : null);

    const dialogRef = useRef<HTMLDivElement>(null);
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const closedRef = useRef(false);

    const closeModal = useCallback(() => {
        setCompleteCancelled(false);
        if (!closedRef.current) {
            closedRef.current = true;
            const previous = previouslyFocusedRef.current;
            previouslyFocusedRef.current = null;
            focusIfConnected(previous);
        }
        Props.onClose();
    }, [Props]);
    const closeModalRef = useRef(closeModal);
    closeModalRef.current = closeModal;

    useLayoutEffect(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body && !dialogRef.current?.contains(active)) {
            previouslyFocusedRef.current = active;
        }
        backButtonRef.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (closedRef.current) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModalRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const root = dialogRef.current;
            if (!root) return;
            const tabbable = tabbableWithin(root);
            if (tabbable.length === 0) {
                event.preventDefault();
                return;
            }
            const [first] = tabbable;
            const last = tabbable.reduce((_, current) => current);
            const { activeElement } = document;
            const inside = activeElement instanceof Node && root.contains(activeElement);
            if (event.shiftKey) {
                if (!inside || activeElement === first) {
                    event.preventDefault();
                    last.focus();
                }
                return;
            }
            if (!inside || activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            const previous = previouslyFocusedRef.current;
            previouslyFocusedRef.current = null;
            focusIfConnected(previous);
        };
    }, []);

    const jobId = Props.jobDetailsId;
    const [jobDetail, setJobDetail] = useState<any>({});
    const [jobNotFound, setJobNotFound] = useState(false);
    const [jobLoadError, setJobLoadError] = useState('');
    const [shopAddress, setShopAddress] = useState<any>({});
    const [shopPhone, setShopPhone] = useState<any>('');
    const [shopLoadError, setShopLoadError] = useState('');
    const [rejectedNotes, setRejectedNotes] = useState<any>('not specified');
    const [writeBusy, setWriteBusy] = useState(false);
    const [writeError, setWriteError] = useState('');
    const writeLock = useRef(false);

    useEffect(() => {
        setCompleteCancelled(false);
    }, [jobId]);

    useEffect(() => {
        return jobsRef.doc(jobId).onSnapshot(
            (documentSnapshot) => {
                const data = documentSnapshot.data();
                if (documentSnapshot.exists === false || data == null) {
                    setJobNotFound(true);
                    setJobLoadError('');
                    setJobDetail({});
                    return;
                }
                setJobNotFound(false);
                setJobLoadError('');
                setJobDetail(data);
            },
            (error) => {
                console.error('This job could not be loaded.', error);
                setJobNotFound(false);
                setJobLoadError(acceptedJobWriteMessage(error, 'This job could not be loaded.'));
                setJobDetail({});
            },
        );
    }, [jobId, jobsRef]);

    const completeJobs = useMemo(() => {
        return jobDetail;
    }, [jobDetail]);

    useEffect(() => {
        const shopId = typeof completeJobs?.shop === 'string' ? completeJobs.shop.trim() : '';
        if (!shopId || typeof shopsRef?.doc !== 'function') {
            setShopAddress(undefined);
            setShopPhone('');
            setShopLoadError('');
            return undefined;
        }
        return shopsRef.doc(shopId).onSnapshot(
            (documentSnapshot) => {
                const shop = documentSnapshot?.data?.();
                setShopAddress(shop?.address);
                setShopPhone(shop?.phone ?? '');
                setShopLoadError('');
            },
            (error) => {
                console.error('Unable to load shop', error);
                setShopAddress(undefined);
                setShopPhone('');
                setShopLoadError(acceptedJobWriteMessage(error, 'Shop could not load.'));
            },
        );
    }, [completeJobs.shop, shopsRef]);

    const acceptWorkOrder = async () => {
        if (writeLock.current) return;
        writeLock.current = true;
        setWriteBusy(true);
        setWriteError('');

        try {
            const techRef = techsRef.doc(techId);
            const jobRef = jobsRef.doc(jobId);

            // One transaction so a reassignment or unassign cannot activate the job
            // for someone else, and rejectedJobs cleanup commits with the status change.
            // arrayRemove needs the exact server rejectedJobs value, read here — never
            // the modal's in-memory snapshot.
            await db.runTransaction(async (transaction) => {
                const jobSnap = await transaction.get(jobRef);
                const techSnap = await transaction.get(techRef);
                const jobData = jobSnap.data() as { status?: unknown; assignee?: unknown } | undefined;
                const techData = techSnap.data() as { rejectedJobs?: unknown } | undefined;
                if (jobSnap.exists === false || jobData == null || jobData.status !== 'pending') {
                    throw new Error(NOT_PENDING);
                }
                if (jobData.assignee !== techId) {
                    throw new Error(NOT_ASSIGNED);
                }

                const matches = rejectedEntriesForJob(techData?.rejectedJobs, jobId);
                if (matches.length > 0) {
                    transaction.update(techRef, {
                        rejectedJobs: firebase.firestore.FieldValue.arrayRemove(...matches),
                    });
                }
                transaction.update(jobRef, {
                    status: 'active',
                    rejectedBy: firebase.firestore.FieldValue.arrayRemove(techId),
                });
            });
        } catch (error) {
            console.error(ACCEPT_WRITE_ERROR, error);
            setWriteError(acceptedJobWriteMessage(error, ACCEPT_WRITE_ERROR));
        } finally {
            writeLock.current = false;
            setWriteBusy(false);
        }
    };

    const completeWorkOrder = async () => {
        if (jobNotFound) return;
        if (regionError || !canCallCompleteJob({ regionId, jobId, status: jobDetail.status })) return;
        setCompleteCancelled(false);
        if (typeof window !== 'undefined' && !window.confirm(PWA_COMPLETE_CONFIRM)) {
            setCompleteError('');
            setCompleteCancelled(true);
            return;
        }
        setCompleteBusy(true);
        setCompleteError('');
        try {
            await completePwaJob({
                callable: (data) => functions.httpsCallable('completeJob')(data),
                regionId,
                jobId,
            });
        } catch (error) {
            setCompleteError(rejectionMessage(error, 'completeJob failed.'));
        } finally {
            setCompleteBusy(false);
        }
    };

    const rejectWorkOrder = async (jobsInformation) => {
        if (writeLock.current) return;
        writeLock.current = true;
        setWriteBusy(true);
        setWriteError('');

        let committed = false;
        try {
            const rejectedEntry = rejectedListEntry(jobsInformation, jobId);
            const techRef = techsRef.doc(techId);
            const jobRef = jobsRef.doc(jobId);

            // One transaction so a reassignment or a failed commit cannot clear the new
            // assignee, drop a concurrent note, or leave the stale-entry cleanup behind.
            // arrayRemove and arrayUnion cannot share rejectedJobs in one update, so a
            // stale entry is removed in an earlier update in this same transaction.
            await db.runTransaction(async (transaction) => {
                const jobSnap = await transaction.get(jobRef);
                const techSnap = await transaction.get(techRef);
                const jobData = jobSnap.data() as
                    | { status?: unknown; assignee?: unknown; notes?: unknown }
                    | undefined;
                const techData = techSnap.data() as { rejectedJobs?: unknown } | undefined;
                if (jobSnap.exists === false || jobData == null || jobData.status !== 'pending') {
                    throw new Error(NOT_PENDING);
                }
                if (jobData.assignee !== techId) {
                    throw new Error(NOT_ASSIGNED);
                }

                const stale = staleRejectedEntries(techData?.rejectedJobs, jobId, rejectedEntry);
                if (stale.length > 0) {
                    transaction.update(techRef, {
                        rejectedJobs: firebase.firestore.FieldValue.arrayRemove(...stale),
                    });
                }
                transaction.update(techRef, {
                    rejectedJobs: firebase.firestore.FieldValue.arrayUnion(rejectedEntry),
                    jobs: firebase.firestore.FieldValue.arrayRemove(jobId),
                });
                transaction.update(jobRef, {
                    assignee: '',
                    notes: rejectionNotes(jobData.notes, tech?.name, rejectedNotes),
                    rejectedBy: firebase.firestore.FieldValue.arrayUnion(techId),
                });
            });
            committed = true;
        } catch (error) {
            console.error(REJECT_WRITE_ERROR, error);
            setWriteError(acceptedJobWriteMessage(error, REJECT_WRITE_ERROR));
        } finally {
            writeLock.current = false;
            setWriteBusy(false);
        }
        if (committed) Props.onClose();
    };

    const onChangeHandler = (setFunc) => (evt) => setFunc(evt.currentTarget.value);
    const shopTel = shopPhoneE164(shopPhone);

    return (
        <>
            <div
                ref={dialogRef}
                className="fixed w-full h-full top-0 left-0 flex z-50 "
                data-testid="accepted-job-modal"
                data-complete-surface={completeCancelled ? 'cancelled' : 'job'}
                role="dialog"
                aria-modal="true"
                aria-label="Job details"
                tabIndex={-1}
            >
                <div
                    className="fixed w-full overflow-y-scroll bg-details-background bg-cover pb-12"
                    // eslint-disable-next-line react-native/no-inline-styles
                    style={{ height: '100%' }}
                >
                    <div className="fixed justify-start p-2 border-b border-solid border-gray-300 bg-gray-100 w-full">
                        <button
                            ref={backButtonRef}
                            className="text-blue-500 background-transparent font-bold text-sm outline-none focus:outline-none"
                            type="button"
                            onClick={closeModal}
                        >
                            <FontAwesomeIcon className="text-base mr-1" icon={faChevronLeft} />
                            <span>Back</span>
                        </button>
                    </div>
                    <div className="pt-14 container mx-auto h-auto text-left pb-4">
                        {jobNotFound ? (
                            <p className="text-xl font-medium text-center" data-testid="accepted-job-not-found">
                                Job not found
                            </p>
                        ) : null}
                        {!jobNotFound ? (
                            <div className="flex flex-col items-center">
                                {jobLoadError ? (
                                    <p
                                        className="mt-2 mx-4 text-sm text-red-600"
                                        role="alert"
                                        data-testid="accepted-job-load-error"
                                    >
                                        {jobLoadError}
                                    </p>
                                ) : null}
                                <div className="uppercase text-xl font-medium px-3">
                                    {jobDetail.status} {jobDetail.type} Job
                                </div>
                                <div className="text-3xl">
                                    {jobDetail.vehicle?.year} {jobDetail.vehicle?.make} {jobDetail.vehicle?.model}
                                </div>
                                {shopLoadError ? (
                                    <p
                                        className="mt-2 mx-4 text-sm text-red-600"
                                        role="alert"
                                        data-testid="accepted-job-shop-error"
                                    >
                                        {shopLoadError}
                                    </p>
                                ) : (
                                    <div className="mt-2 mx-4 text-sm text-gray-600 uppercase">
                                        {shopAddress?.street}, {shopAddress?.city}, {shopAddress?.state}
                                    </div>
                                )}
                                {shopPhone && (
                                    <div className="mt-2 mx-4 text-lg text-blue-500 uppercase">
                                        <a href={shopTel ? `tel:${shopTel}` : undefined}>
                                            <FontAwesomeIcon className="mr-1" icon={faPhone} />
                                            {shopPhone}
                                        </a>
                                    </div>
                                )}
                                <div className="mt-2 md:max-w-lg max-w-sm">
                                    <img
                                        src={jobDetail.status === 'complete' ? CarComplete : Car}
                                        alt="Tech Scheduler Car"
                                    />
                                </div>
                                <div className="mt-4 text-base text-gray-600 text-center">
                                    <p className="my-1">Insurance: {jobDetail.vehicle?.insurance}</p>
                                    <p className="my-1">Contact: {jobDetail.contact}</p>
                                    <p className="my-1">
                                        Date: {jobDetail.date ? ordersJobDateTimeLabel(jobDetail) : ''}
                                    </p>
                                    <p className="my-1">Drop-off: {jobDetail.dropped}</p>
                                    <p className="my-1">
                                        {jobDetail.status === 'complete' &&
                                            (jobDetail.waiting ? 'Available to pick up' : 'Not available to pick up')}
                                    </p>
                                    <p className="my-1 max-w-xl">Notes: {jobDetail.notes}</p>
                                </div>
                                <div className="mt-4">
                                    {completeCopy ? (
                                        <StatusEmptyCard
                                            copy={completeCopy}
                                            onAction={() => void completeWorkOrder()}
                                        />
                                    ) : null}
                                    {jobDetail.status === 'pending' ? (
                                        <button
                                            type="button"
                                            className="bg-green-400 hover:bg-green-900 text-white font-bold py-3 px-4 rounded w-72"
                                            disabled={writeBusy}
                                            onClick={() => void acceptWorkOrder()}
                                        >
                                            Accept Job
                                        </button>
                                    ) : null}
                                    {jobDetail.status === 'active' ? (
                                        <button
                                            type="button"
                                            className="bg-green-400 hover:bg-green-900 text-white font-bold py-3 px-4 rounded w-72"
                                            data-testid="pwa-complete-job"
                                            disabled={
                                                completeBusy ||
                                                Boolean(regionError) ||
                                                !canCallCompleteJob({ regionId, jobId, status: jobDetail.status })
                                            }
                                            onClick={() => void completeWorkOrder()}
                                        >
                                            {completeBusy ? PWA_COMPLETE_BUSY : 'Complete Job'}
                                        </button>
                                    ) : null}
                                    {regionError || completeError ? (
                                        <p className="mt-2 text-sm text-red-600" data-testid="pwa-complete-error">
                                            {regionError || completeError}
                                        </p>
                                    ) : null}
                                    {writeError ? (
                                        <p
                                            className="mt-2 text-sm text-red-600"
                                            role="alert"
                                            data-testid="accepted-job-write-error"
                                        >
                                            {writeError}
                                        </p>
                                    ) : null}
                                    {jobDetail.status === 'complete' ? (
                                        <button
                                            type="button"
                                            disabled
                                            className="bg-gray-200 hover:bg-gray-200 text-white font-bold py-3 px-4 rounded w-72"
                                        >
                                            Job Complete
                                        </button>
                                    ) : null}
                                </div>
                                <div className="mt-8 mb-4 text-center">
                                    {jobDetail.status === 'pending' && (
                                        <div className="flex flex-col">
                                            <button
                                                type="button"
                                                className="bg-red-400 hover:bg-red-900 text-white font-bold py-3 px-4 rounded w-72"
                                                disabled={writeBusy}
                                                onClick={() => void rejectWorkOrder(jobDetail)}
                                            >
                                                Reject Job
                                            </button>
                                            <input
                                                type="text"
                                                placeholder="Reason for rejection"
                                                onChange={onChangeHandler(setRejectedNotes)}
                                                className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AcceptedJobModal;
